import React from 'react';
import { Progress } from '@/components/ui/progress';
import { Card } from '@/components/ui/card';
import { CheckCircle, Loader2 } from 'lucide-react';

interface FixProgressProps {
  progress: number;
  totalIssues: number;
}

const FixProgress: React.FC<FixProgressProps> = ({ progress, totalIssues }) => {
  const fixedIssues = Math.floor((progress / 100) * totalIssues);
  const remainingIssues = totalIssues - fixedIssues;

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-2xl font-semibold mb-2">AI Fix in Progress</h2>
        <p className="text-muted-foreground">
          Our AI is automatically fixing your website issues
        </p>
      </div>

      <Card className="p-6">
        <div className="space-y-4">
          {/* Progress Bar */}
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span>Fixing Issues</span>
              <span>{progress}%</span>
            </div>
            <Progress value={progress} className="h-3" />
          </div>

          {/* Stats */}
          <div className="grid grid-cols-3 gap-4 text-center">
            <div>
              <div className="text-2xl font-bold text-green-600">{fixedIssues}</div>
              <div className="text-sm text-muted-foreground">Fixed</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-yellow-600">{remainingIssues}</div>
              <div className="text-sm text-muted-foreground">Remaining</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-blue-600">{totalIssues}</div>
              <div className="text-sm text-muted-foreground">Total</div>
            </div>
          </div>

          {/* Status Message */}
          <div className="text-center">
            <div className="flex items-center justify-center gap-2 mb-2">
              <Loader2 className="h-5 w-5 animate-spin text-primary" />
              <span className="font-medium">AI is working...</span>
            </div>
            <p className="text-muted-foreground text-sm">
              {progress < 100 
                ? `Fixing issue ${fixedIssues + 1} of ${totalIssues}...`
                : 'All issues have been fixed!'
              }
            </p>
          </div>

          {/* Fixing Steps */}
          <div className="space-y-2">
            <h4 className="font-medium text-center mb-3">What's happening:</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
              {[
                { step: 'Analyzing Issues', completed: progress > 10 },
                { step: 'Generating Fixes', completed: progress > 30 },
                { step: 'Applying Changes', completed: progress > 60 },
                { step: 'Verifying Results', completed: progress > 90 },
                { step: 'Finalizing', completed: progress >= 100 },
                { step: 'Complete', completed: progress >= 100 }
              ].map((item, index) => (
                <div 
                  key={index}
                  className={`flex items-center gap-2 p-2 rounded ${
                    item.completed 
                      ? 'bg-green-50 text-green-700' 
                      : 'bg-gray-50 text-gray-500'
                  }`}
                >
                  {item.completed ? (
                    <CheckCircle className="h-4 w-4 text-green-500" />
                  ) : (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  )}
                  <span>{item.step}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Completion Message */}
          {progress >= 100 && (
            <div className="text-center bg-green-50 p-4 rounded-lg">
              <CheckCircle className="h-8 w-8 text-green-500 mx-auto mb-2" />
              <h3 className="font-semibold text-green-800 mb-1">
                All Issues Fixed!
              </h3>
              <p className="text-green-600 text-sm">
                Your website has been optimized successfully.
              </p>
            </div>
          )}
        </div>
      </Card>
    </div>
  );
};

export default FixProgress;
